import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@progress/kendo-react-dialogs';
import IconPicker, { icons } from '../../Shared/IconPicker/IconPicker';
import Color from "../../Helpers/color";
import { callApiWithAuthorization } from '../../../API/CallApi';
import { OverlayLoader } from "../../Shared/Loader/OverlayLoader"
import EntityRecommendationContext, { EntityRecommendationContextProvider } from '../../../Context/EntityRecommendation/EntityRecommendationContext';
import { RecommendedEntityGrid } from '../../Entities/Recommendation/EntityRecommendation';
import { RecommendedLinkedEntityGrid } from '../../LinkedEntities/Recommendation/EntityRelationsRecommendation';
import useImportSelectedRecommendationData from '../../../Hooks/Recommendation/useImportSelectedRecommendationData';
import { useNavigate } from 'react-router-dom';
import SolutionLibrary from '../SolutionLibrary/SolutionLibrary';
import { useContext } from 'react';
import { getDatalakeImageUrl } from '../../Helpers/DateLakeStorage';
import { ENTITY_RELATION_PLURAL_LABEL } from '../../Helpers/Labels/EntityRelationLabel';
import { ENTITY_PLURAL_LABEL } from '../../Helpers/Labels/EntityLabel';

function CreateProjectDialog({
    projects,
    organization,
    user,
    onCreate,
    onCancel,
    onProjectLimitReached,
    onClose,
    isShowFromRuntime,
}) {

    const navigate = useNavigate();
    const [title, setTitle] = useState("Recommended Library Apps");

    function handleCreateProjectResponse(response) {
        if (response.message === "Max apps allowed limit reached") {
            onProjectLimitReached();
        } else {
            onCreate(response);
        }
    }

    async function createProject(projectName, icon) {
        try {
            let color = Color(projects.length + 1);
            let properties = {
                organizationName: organization.number,
                organizationId: organization.id,
                email: user.email,
                projectName: projectName.trim().replace(/\s\s+/g, " "),
                Icon: icon,
                developerMode: false,
                abbreviation: "",
                bgColor: color,
                description: "",
                tag: "",
                hasSchemaChanges: false,
                isPublished: false,
                isSuperApp: false,
                isFeaturedApp: false,
                TilesImages: "",
                SlideshowImages: "",
            };

            properties.projectUrl = properties.projectName.replace(/[^a-zA-Z0-9\~\_\-\.]/g, "")

            if (organization.id === process.env.REACT_APP_LIBRARY_ORGANIZATION_ID) {
                properties.tagsForRecommendation = "";
            }

            const request = await callApiWithAuthorization("/api/v0/createProjectandRole", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(properties),
            });
            const response = await request.json();

            return response;
        } catch (e) {
            console.error(e);
        }
    }

    function navigateToProject(project) {
        navigate(`/${organization.number}/${project.name}/Entities`);
    }

    return (
        <Dialog
            width={"75%"}
            height={"75%"}
            title={title}
            onClose={onClose}>
            <EntityRecommendationContextProvider>
                <CreateProjectWithRecommendation
                    isShowFromRuntime={isShowFromRuntime}
                    createProject={createProject}
                    projects={projects}
                    user={user}
                    organization={organization}
                    onCancel={onCancel}
                    onTitleChange={setTitle}
                    onSuccessfulImport={navigateToProject}
                    onProjectLimitReached={onProjectLimitReached} />
            </EntityRecommendationContextProvider>
            {/* <CreateProjectForm
                projects={projects}
                organization={organization}
                user={user}
                onCancel={onCancel}
                onAddRequestCompleted={handleCreateProjectResponse} /> */}
        </Dialog>
    );
};

export function CreateProjectWithRecommendation({
    projects, organization, user,
    onCancel, createProject, onTitleChange, onSuccessfulImport, onProjectLimitReached, isShowFromRuntime
}) {

    const navigate = useNavigate();

    const [currentTitle, setCurrentTitle] = useState("Alice AI Recommendation");

    const [projectTypes, setProjectTypes] = useState([]);
    const [customDomains, setCustomDomains] = useState([]);

    const [showProjectTypeSelection, setShowProjectTypeSelection] = useState(false);
    const [showLibraryProjects, setShowLibraryProjects] = useState(isShowFromRuntime ? true : false);
    const [showRecommendedEntityGrid, setShowRecommendedEntityGrid] = useState(false);
    const [showRecommendedRelationGrid, setShowRecommendedRelationGrid] = useState(false);
    const [showCreateProjectForm, setShowCreateProjectForm] = useState(isShowFromRuntime ? false : true);

    const [includeLibraryProjects, setIncludeLibraryProjects] = useState(true);

    const [project, setProject] = useState({});
    const { importData, response, error } = useImportSelectedRecommendationData(organization.id, project.id);

    const [projectName, setProjectName] = useState("");
    const [icon, setIcon] = useState(icons[0]);

    const { state: recommendationState, dispatch } = useContext(EntityRecommendationContext);

    // every time currentTitle changes, call the onTitleChange function
    useEffect(() => {
        onTitleChange(currentTitle);
    }, [currentTitle]);

    useEffect(() => {
        if (project && project.id) {
            const showRecommendationDialog = !!recommendationState.data.find(element => element.selected);
            if (recommendationState.data.some(element => element.selected)) {
                importData()
                    .then(() => onSuccessfulImport({
                        ...project,
                        showRecommendationDialog
                    }))
                    .then(() => {
                        dispatch({
                            type: "SET_CREATING_DATA",
                            payload: false
                        })
                    });
            } else {
                onSuccessfulImport({
                    ...project,
                    showRecommendationDialog
                })
                dispatch({
                    type: "SET_CREATING_DATA",
                    payload: false
                })
            }
        } else if (project.status === "Failure" && project.message === "Max apps allowed limit reached") {
            onProjectLimitReached();
        }
    }, [project])

    function navigateToProject(projectName) {
        const showRecommendationDialog = !!recommendationState.data.find(element => element.selected);
        navigate(`/${organization.number}/${projectName}/Entities?showRecommendationDialog=${showRecommendationDialog}`);
    }

    if (recommendationState.creatingData) {
        return (
            <div className="d-flex flex-column justify-content-enter align-items-center w-100 h-100">
                <div style={{ height: "80%" }}>
                    <img
                        alt="loader-clone"
                        height={"100%"}
                        src={`${process.env.REACT_APP_DATALAKESTORAGE_URL}/${process.env.REACT_APP_DATALAKESTORAGE_RESOURCES}/images/loaders/library.gif`} />
                </div>
                <h3>Please wait while we are creating your app ...</h3>
            </div>
        )
    }
    
    if (includeLibraryProjects && showLibraryProjects) {
        const domains = projectTypes.filter(type => type.isSelected).map(type => type.text);
        const tags = Array.from(new Set([...domains, ...customDomains]));

        return (
            <SolutionLibrary
                isShowFromRuntime={true}
                showCreateProjectPage={() => {
                    setShowLibraryProjects(false);
                    setShowCreateProjectForm(true);
                    setCurrentTitle("Create Project");
                }}
                user={user}
                currentOrganization={organization}
                organizations={[organization]}
                onAppImport={({ name }) => navigateToProject(name)} />
        )
    }

    if (showRecommendedEntityGrid) {
        return (
            <RecommendedEntityGrid
                usedInCreateProject
                entities={[]}
                projectType={projectTypes}
                fetchUsingUserInput={false}
                userInput={[]}
                projectId={""}
                organizationId={organization.id}
                onEntityLimitReached={() => { }}
                onBack={() => {
                    if (!isShowFromRuntime) {
                        setShowCreateProjectForm(true);
                        setCurrentTitle("Alice AI Recommendation")
                    } else if (includeLibraryProjects) {
                        setShowLibraryProjects(true);
                        setCurrentTitle("Alice AI Recommendation")
                    } else {
                        setShowCreateProjectForm(true);
                        setCurrentTitle("Select Project Category")
                    }
                    setShowRecommendedEntityGrid(false);
                }}
                onNext={() => {
                    setCurrentTitle(`Alice Recommendation - ${ENTITY_RELATION_PLURAL_LABEL}`);
                    setShowRecommendedRelationGrid(true);
                    setShowRecommendedEntityGrid(false);
                }}
            // onEntitiesLoaded={(entities) => {
            //     if (entities.length === 0) {
            //         setShowCreateProjectForm(true);
            //         setShowRecommendedEntityGrid(false);
            //     }
            // }}
            />
        )
    }

    if (showRecommendedRelationGrid) {
        return (
            <RecommendedLinkedEntityGrid
                usedInWorkspace
                organizationId={organization.id}
                projectId={""}
                entities={[]}
                onBack={() => {
                    setShowRecommendedEntityGrid(true);
                    setShowRecommendedRelationGrid(false);
                }}
                onAdd={() => {
                    createProject(projectName, icon)
                        .then(setProject)
                }} />
        )
    }

    async function handleNextInCreateProjectForm(projectName, icon) {
        setProjectName(projectName);
        setIcon(icon);
        setProjectTypes([
            { text: projectName, isSelected: true }
        ])
        setShowRecommendedEntityGrid(true);
        setShowCreateProjectForm(false);
        setCurrentTitle(`Alice Recommendation - ${ENTITY_PLURAL_LABEL}`)
    }

    if (showCreateProjectForm) {
        return (
            <CreateProjectForm
                initialProjectName={projectName}
                initialProjectIcon={icon}
                projects={projects}
                organization={organization}
                user={user}
                onAddRequestCompleted={(project) => {
                    setProject(project)
                }}
                onCreate={handleNextInCreateProjectForm}
                onCancel={onCancel} />
        )
    }

    return (
        <div>
            Loading....
        </div>
    )
}

export function CreateProjectForm({
    projects, organization, user, initialProjectName = "", initialProjectIcon = "",
    onAddRequestCompleted, onCreate, onCancel
}) {

    const [loading, setLoading] = useState(false);
    const [projectName, setProjectName] = useState(initialProjectName);
    const [icon, setIcon] = useState(initialProjectIcon);
    const [nameError, setNameError] = useState("");

    const submitBtn = useRef(null);

    const handleProjectName = (e) => {
        const { value } = e.target;
        if (value.length < 65) {
            const isNameUsed = projects.findIndex(project => project.name.trim().toLowerCase() === value.trim().toLowerCase()) > -1;
            const nameHasSpecialCharacters = /[^a-zA-Z0-9 ]/g.test(value);
            const nameStartsWithNumber = /^\d/.test(value);
            let error = "";
            if (isNameUsed) {
                error = "Application with that name already exists"
            } else if (nameHasSpecialCharacters) {
                error = "Application name cannot have special character";
            } else if (nameStartsWithNumber) {
                error = "Application name cannot start with number";
            }
            setNameError(error);
            setProjectName(value);
        }
    }

    async function handleNext(e) {
        e.preventDefault();
        setLoading(true);
        onCreate(projectName, icon);
    }

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();
            submitBtn.current.disabled = true;
            setLoading(true);
            let color = Color(projects.length + 1);

            let properties = {
                organizationName: organization.number,
                organizationId: organization.id,
                email: user.email,
                projectName: projectName.trim().replace(/\s\s+/g, " "),
                Icon: icon,
                developerMode: false,
                abbreviation: "",
                bgColor: color,
                description: "",
                tag: "",
                hasSchemaChanges: false,
                isPublished: false,
                isSuperApp: false,
                isFeaturedApp: false,
                TilesImages: "",
                SlideshowImages: "",
            };

            properties.projectUrl = properties.projectName.replace(/[^a-zA-Z0-9\~\_\-\.]/g, "")

            if (organization.id === process.env.REACT_APP_LIBRARY_ORGANIZATION_ID) {
                properties.tagsForRecommendation = "";
            }

            const request = await callApiWithAuthorization("/api/v0/createProjectandRole", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(properties),
            });
            const response = await request.json();

            onAddRequestCompleted(response);
        } catch (e) {
            console.error(e);
        } finally {
            e.target.disabled = false;
        }
    }

    return (
        <form
            className='container-fluid h-75'
            onSubmit={handleNext}
        >
            <div className='row h-100 mt-4'>
                <div className='col-6 mt-4'>
                    <div className='row'>
                        <div className='col-12'>
                            <label
                                htmlFor="project-name-field"
                                className="form-label d-inline-block"
                                required>
                                <span>Please name your application. e.g. Sales Management,
                                Project Management and so on.</span>
                            </label>
                            &nbsp;
                            {
                                nameError &&
                                <span className='text-danger'>
                                    {nameError}
                                </span>
                            }
                            <input
                                id="project-name-field"
                                className="name-text form-control"
                                maxLength="64"
                                value={projectName}
                                onChange={handleProjectName}
                                autoFocus={true}
                            />
                        </div>
                        <div className='col-12'>
                            <label className="form-label d-inline-block">
                                Icon
                            </label>
                            <IconPicker
                                onChange={setIcon}
                                selected={icon} />
                        </div>
                        <div className='col-12 mt-4 pt-4'>
                            <div className='alert create-project-help-alert mt-4 p-4'>
                                Based on the name you provide, we will make recommendations using our Alice AI.
                            </div>
                            <div className='d-flex w-100 justify-content-end mt-2'>
                                <button
                                    ref={submitBtn}
                                    type='submit'
                                    className="btn btn-primary"
                                    disabled={
                                        projectName.trim().length === 0 ||
                                        nameError.length > 0
                                    }>
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='col-6 d-flex justify-content-center align-items-center'>
                    <img
                        height="300px"
                        src={getDatalakeImageUrl("alice.svg")} />
                </div>
            </div>
            {
                loading && <OverlayLoader />
            }
        </form>
    )
}

CreateProjectDialog.defaultProps = {
    projects: []
}

CreateProjectForm.defaultProps = {
    projects: []
}

CreateProjectDialog.propTypes = {
    organization: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    projects: PropTypes.array.isRequired,
    onCreate: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onProjectLimitReached: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
}

export default CreateProjectDialog;